import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, Briefcase, Calendar, FileText, Mail, CheckCircle, DollarSign, Clipboard, ThumbsUp, Activity } from 'lucide-react';
import { customersAPI } from '../lib/api';
import { formatTimeAgo, getTenantTimezone, parseUTC } from '../lib/timezone';
import { useNavigate } from 'react-router-dom';

const ICON_MAP = {
  briefcase: Briefcase,
  calendar: Calendar,
  'file-text': FileText,
  mail: Mail,
  'check-circle': CheckCircle,
  'dollar-sign': DollarSign,
  clipboard: Clipboard,
  'thumbs-up': ThumbsUp,
};

const TYPE_COLORS = {
  job_created: 'border-l-blue-500',
  job_scheduled: 'border-l-purple-500',
  invoice_created: 'border-l-amber-500',
  invoice_sent: 'border-l-cyan-500',
  invoice_paid: 'border-l-emerald-500',
  payment_received: 'border-l-green-500',
  estimate_created: 'border-l-orange-500',
  estimate_approved: 'border-l-emerald-500',
};

function formatTimestamp(iso) {
  return formatTimeAgo(iso);
}

function getTimeGroup(iso) {
  if (!iso) return 'Earlier';
  const d = parseUTC(iso);
  const now = new Date();
  const tz = getTenantTimezone();
  const dateInTz = new Date(d.toLocaleString('en-US', { timeZone: tz }));
  const nowInTz = new Date(now.toLocaleString('en-US', { timeZone: tz }));
  const todayStart = new Date(nowInTz.getFullYear(), nowInTz.getMonth(), nowInTz.getDate());
  const weekAgo = new Date(todayStart.getTime() - 7 * 86400000);
  if (dateInTz >= todayStart) return 'Today';
  if (dateInTz >= weekAgo) return 'This Week';
  return 'Earlier';
}

export default function CustomerActivityFeed({ customerId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    customersAPI.getActivity(customerId, { limit: 50 })
      .then(res => setEvents(res.data?.events || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [customerId]);

  if (loading) {
    return (
      <Card data-testid="activity-feed-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Activity className="w-4 h-4" /> Activity Feed
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 py-1 animate-pulse">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-muted shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3.5 bg-muted rounded w-2/3" />
                  <div className="h-2.5 bg-muted rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (events.length === 0) {
    return (
      <Card data-testid="activity-feed-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Activity className="w-4 h-4" /> Activity Feed
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center py-8 text-center">
            <div className="w-10 h-10 rounded-full bg-muted/60 flex items-center justify-center mb-3">
              <Activity className="w-5 h-5 text-muted-foreground/50" />
            </div>
            <p className="text-sm font-medium mb-1">No activity yet</p>
            <p className="text-xs text-muted-foreground max-w-[260px]">
              Jobs, invoices, estimates, and payments for this customer will appear here.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Group events by time period
  const groups = [];
  let currentGroup = null;
  for (const event of events) {
    const group = getTimeGroup(event.timestamp);
    if (group !== currentGroup) {
      currentGroup = group;
      groups.push({ label: group, events: [] });
    }
    groups[groups.length - 1].events.push(event);
  }

  return (
    <Card data-testid="activity-feed-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Activity className="w-4 h-4" /> Activity Feed
          <Badge variant="secondary" className="text-xs ml-1">{events.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {groups.map((group, gi) => (
          <div key={group.label}>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">{group.label}</p>
            <div className="relative ml-3">
              <div className="absolute left-0 top-0 bottom-0 w-px bg-border" />
              {group.events.map((event, i) => {
                const Icon = ICON_MAP[event.icon] || FileText;
                return (
                  <div
                    key={`${event.type}-${event.timestamp}-${gi}-${i}`}
                    className="relative flex items-start gap-3 pb-3 last:pb-0"
                    data-testid={`activity-event-${event.type}`}
                  >
                    <div className="absolute -left-[5px] top-0.5 w-[10px] h-[10px] rounded-full bg-background border-2 border-muted-foreground/30 z-10" />
                    <div className="ml-4 flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <p className="text-sm leading-snug">{event.description}</p>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {event.link && (
                          <button
                            className="text-xs text-primary hover:underline"
                            onClick={() => navigate(event.link)}
                            data-testid={`activity-link-${event.type}-${i}`}
                          >
                            {event.link_label}
                          </button>
                        )}
                        {event.amount != null && (
                          <span className="text-xs font-medium text-muted-foreground">${event.amount.toFixed(2)}</span>
                        )}
                        <span className="text-[11px] text-muted-foreground/70">{formatTimestamp(event.timestamp)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {gi < groups.length - 1 && <div className="border-t mt-3" />}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
